import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Account } from '@/types/account';
import type { PlatformCountry } from '@/platformCountry';
import systemAdminAccountsApi from '@/api/modules/systemAdminAccounts';

export const useSystemAdminAccountStore = defineStore('system-admin-account', () => {
  const accounts = ref<Account[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchAccounts = async () => {
    isLoading.value = true;
    error.value = null;
    try {
      accounts.value = await systemAdminAccountsApi.getAccounts();
    } catch (e: any) {
      error.value = e.message;
    } finally {
      isLoading.value = false;
    }
  };

  const createAccount = async (data: { name: string; cnpj: string; platform_country_code: PlatformCountry }) => {
    isLoading.value = true;
    error.value = null;
    try {
      const newAccount = await systemAdminAccountsApi.createAccount(data);
      accounts.value.push(newAccount);
      return newAccount;
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const deleteAccount = async (accountId: string) => {
    isLoading.value = true;
    error.value = null;
    try {
      await systemAdminAccountsApi.deleteAccount(accountId);
      accounts.value = accounts.value.filter((account) => account.id !== accountId);
    } catch (e: any) {
      error.value = e.message;
    } finally {
      isLoading.value = false;
    }
  };

  const setAccountRegulators = async (accountId: string, slugs: string[]) => {
    isLoading.value = true;
    error.value = null;
    try {
      const regulators = await systemAdminAccountsApi.setAccountRegulators(accountId, slugs);
      const account = accounts.value.find((a) => a.id === accountId);
      if (account) account.regulators = regulators;
      return regulators;
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  const setAccountPlatformCountry = async (accountId: string, platformCountryCode: PlatformCountry) => {
    isLoading.value = true;
    error.value = null;
    try {
      const { platform_country_code } = await systemAdminAccountsApi.setAccountPlatformCountry(
        accountId,
        platformCountryCode,
      );
      const account = accounts.value.find((a) => a.id === accountId);
      if (account) account.platform_country_code = platform_country_code;
      return platform_country_code;
    } catch (e: any) {
      error.value = e.message;
      throw e;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    accounts,
    isLoading,
    error,
    fetchAccounts,
    createAccount,
    deleteAccount,
    setAccountRegulators,
    setAccountPlatformCountry,
  };
});
